import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Sparkles, Loader2, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { http, errorText, getToken, streamSSE } from "@/lib/api";
import { fileUrl } from "@/lib/images";
import { AdminTitle } from "./AdminLayout";

const STATUSES = ["planlagt", "generert – må kontrolleres", "eget foto – venter på godkjenning", "må endres", "faglig kontrollert", "godkjent"];
const inp = "rounded-none h-9 text-sm";

const F = ({ label, children }) => <label className="block"><span className="text-[0.65rem] uppercase tracking-[0.15em] text-[#5C5A56] mb-1 block">{label}</span>{children}</label>;

const ImgUpload = ({ path, onUploaded, testid, pub = false }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const up = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    fd.append("public", pub ? "true" : "false");
    try { const { data } = await http.post("/admin/book-images", fd); onUploaded(data.storage_path); } catch (err) { toast.error(errorText(err)); }
    setBusy(false);
  };
  return (
    <div className="w-36 shrink-0">
      {path ? <img src={fileUrl(path, getToken())} alt="" className="w-36 h-24 object-cover" /> : <div className="w-36 h-24 bg-[#F0EBE1]" />}
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={up} />
      <button onClick={() => ref.current.click()} className="mt-1 w-full flex items-center justify-center gap-1 text-xs py-1 border border-[#E5E0D8] hover:border-[#1A1A1A]" data-testid={testid}>
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}Bytt bilde
      </button>
    </div>
  );
};

const StepEditor = ({ s, onChange, onDelete }) => {
  const set = (k) => (e) => onChange({ ...s, [k]: e.target.value });
  return (
    <div className="flex gap-4 py-4 border-b border-[#E5E0D8]" data-testid={`step-editor-${s.code}`}>
      <ImgUpload path={s.image_path} onUploaded={(p) => onChange({ ...s, image_path: p })} testid={`step-upload-${s.code}`} />
      <div className="flex-1 grid md:grid-cols-2 gap-3">
        <Input value={s.code} onChange={set("code")} className={inp} placeholder="Bildekode" />
        <Input value={s.title} onChange={set("title")} className={inp} placeholder="Tittel" />
        <Textarea value={s.text} onChange={set("text")} rows={3} className="rounded-none text-sm md:col-span-2" placeholder="Forklaring" data-testid={`step-text-${s.code}`} />
        <Input value={s.caption} onChange={set("caption")} className={inp} placeholder="Bildetekst" />
        <Input value={s.alt} onChange={set("alt")} className={inp} placeholder="Alternativ tekst" />
        <Textarea value={s.image_prompt} onChange={set("image_prompt")} rows={2} className="rounded-none text-xs md:col-span-2" placeholder="Bildeprompt / fotoinstruks" />
        <select value={s.image_status} onChange={set("image_status")} className="h-9 border border-[#E5E0D8] text-sm px-2 bg-white" data-testid={`step-status-${s.code}`}>
          {STATUSES.map((x) => <option key={x}>{x}</option>)}
        </select>
        <button onClick={onDelete} className="justify-self-end p-2 text-[#5C5A56] hover:text-red-700"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
      </div>
    </div>
  );
};

const AiAssist = ({ bookId, chapter, task }) => {
  const [instr, setInstr] = useState("");
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true); setOut("");
    try { await streamSSE(`/admin/books/${bookId}/ai-task`, { chapter_title: chapter.title, task_title: task.title, instruction: instr }, setOut); } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  return (
    <div className="mt-4 p-4 bg-[#F0EBE1]/60 space-y-2" data-testid={`ai-assist-${task.id}`}>
      <div className="flex gap-2">
        <Input value={instr} onChange={(e) => setInstr(e.target.value)} placeholder="Ekstra instruks til AI (valgfritt)" className={`${inp} bg-white`} />
        <button onClick={run} disabled={busy} className="flex items-center gap-2 px-4 bg-[#1A1A1A] text-white text-xs whitespace-nowrap disabled:opacity-50" data-testid={`ai-assist-btn-${task.id}`}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}AI-utkast
        </button>
      </div>
      {out && <Textarea value={out} onChange={(e) => setOut(e.target.value)} rows={10} className="rounded-none text-sm bg-white" data-testid={`ai-assist-output-${task.id}`} />}
      {out && <p className="text-xs text-[#5C5A56]">Kopier det du vil bruke inn i feltene. Alt merket [LIISA SJEKK] må kontrolleres.</p>}
    </div>
  );
};

const TaskEditor = ({ t, chapter, bookId, onChange, onDelete }) => {
  const set = (k) => (e) => onChange({ ...t, [k]: e.target.value });
  const setStep = (i, s) => onChange({ ...t, steps: t.steps.map((x, j) => (j === i ? s : x)) });
  const addStep = () => onChange({ ...t, steps: [...t.steps, { code: `${chapter.id.toUpperCase()}-${t.id.split("-").pop().toUpperCase()}-${String(t.steps.length + 1).padStart(3, "0")}`, title: "", text: "", image_prompt: "", caption: "", alt: "", image_path: "", image_status: "planlagt" }] });
  return (
    <div className="border border-[#E5E0D8] p-5 mt-4" data-testid={`task-editor-${t.id}`}>
      <div className="grid md:grid-cols-4 gap-3">
        <div className="md:col-span-2"><F label="Oppgave"><Input value={t.title} onChange={set("title")} className={inp} /></F></div>
        <F label="Vanskelighetsgrad"><Input value={t.difficulty} onChange={set("difficulty")} className={inp} /></F>
        <F label="Tid"><Input value={t.time} onChange={set("time")} className={inp} /></F>
        <div className="md:col-span-2"><F label="Læringsmål"><Input value={t.goal} onChange={set("goal")} className={inp} /></F></div>
        <div className="md:col-span-2"><F label="Passer for"><Input value={t.suits} onChange={set("suits")} className={inp} /></F></div>
        <div className="md:col-span-4"><F label="Utstyr"><Input value={t.tools} onChange={set("tools")} className={inp} /></F></div>
        <div className="md:col-span-2"><F label="Vanlige feil"><Textarea value={t.mistakes} onChange={set("mistakes")} rows={2} className="rounded-none text-sm" /></F></div>
        <div className="md:col-span-2"><F label="Kontroll av resultat"><Textarea value={t.check} onChange={set("check")} rows={2} className="rounded-none text-sm" /></F></div>
        <div className="md:col-span-3"><F label="Når til skredder"><Input value={t.when_pro} onChange={set("when_pro")} className={inp} /></F></div>
        <F label="Status"><Input value={t.status} onChange={set("status")} className={inp} /></F>
        <F label="Smakebit: antall gratis steg"><Input type="number" min="0" value={t.preview_steps || 0} onChange={(e) => onChange({ ...t, preview_steps: Number(e.target.value) })} className={inp} data-testid={`task-preview-steps-${t.id}`} /></F>
      </div>
      <p className="mt-6 text-sm font-medium">Steg ({t.steps.length})</p>
      {t.steps.map((s, i) => <StepEditor key={i} s={s} onChange={(v) => setStep(i, v)} onDelete={() => onChange({ ...t, steps: t.steps.filter((_, j) => j !== i) })} />)}
      <div className="flex justify-between mt-3">
        <button onClick={addStep} className="flex items-center gap-1 text-xs text-[#8C6E48]" data-testid={`add-step-${t.id}`}><Plus className="w-3 h-3" />Legg til steg</button>
        <button onClick={onDelete} className="text-xs text-[#5C5A56] hover:text-red-700">Slett oppgave</button>
      </div>
      <AiAssist bookId={bookId} chapter={chapter} task={t} />
    </div>
  );
};

const ContentEditor = ({ b, setB }) => {
  const set = (k) => (e) => setB({ ...b, [k]: e.target.value });
  const setCh = (i, c) => setB({ ...b, chapters: b.chapters.map((x, j) => (j === i ? c : x)) });
  return (
    <div>
      <div className="grid md:grid-cols-3 gap-4">
        <F label="Tittel"><Input value={b.title} onChange={set("title")} className={inp} data-testid="book-title-input" /></F>
        <F label="Undertittel"><Input value={b.subtitle} onChange={set("subtitle")} className={inp} /></F>
        <F label="Pris"><Input value={b.price} onChange={set("price")} className={inp} data-testid="book-price-input" /></F>
        <div className="md:col-span-2"><F label="Beskrivelse"><Textarea value={b.description} onChange={set("description")} rows={3} className="rounded-none text-sm" /></F></div>
        <F label="For hvem"><Textarea value={b.for_whom} onChange={set("for_whom")} rows={3} className="rounded-none text-sm" /></F>
      </div>
      <div className="flex flex-wrap gap-8 mt-5 text-sm">
        <label className="flex items-center gap-2"><Switch checked={b.product_page_visible} onCheckedChange={(v) => setB({ ...b, product_page_visible: v })} data-testid="book-visible-toggle" />Produktside synlig</label>
        <label className="flex items-center gap-2"><Switch checked={!!b.price_approved} onCheckedChange={(v) => setB({ ...b, price_approved: v })} data-testid="book-price-approved-toggle" />Pris godkjent (vises på produktsiden)</label>
        <label className={`flex items-center gap-2 ${b.sumup_ready ? "" : "opacity-60"}`}><Switch checked={!!b.payment_active} disabled={!b.sumup_ready} onCheckedChange={(v) => setB({ ...b, payment_active: v })} data-testid="book-payment-toggle" />{b.sumup_ready ? `Nettbetaling SumUp ${b.payment_active ? "– på (krever også godkjent pris)" : "– av"}` : "Nettbetaling (SumUp – venter på hemmelig nøkkel og merchant code)"}</label>
      </div>
      <Accordion type="multiple" className="mt-8">
        {b.chapters.map((c, i) => (
          <AccordionItem key={c.id} value={c.id} data-testid={`chapter-editor-${c.number}`}>
            <AccordionTrigger className="text-left font-serif-display text-xl">{c.number}. {c.title}</AccordionTrigger>
            <AccordionContent>
              <div className="flex gap-4">
                <ImgUpload path={c.image_path} onUploaded={(p) => setCh(i, { ...c, image_path: p })} testid={`chapter-upload-${c.number}`} pub />
                <div className="flex-1 space-y-3">
                  <Input value={c.title} onChange={(e) => setCh(i, { ...c, title: e.target.value })} className={inp} />
                  <Textarea value={c.intro} onChange={(e) => setCh(i, { ...c, intro: e.target.value })} rows={3} className="rounded-none text-sm" />
                </div>
              </div>
              {c.tasks.map((t, k) => (
                <TaskEditor key={t.id} t={t} chapter={c} bookId={b.id}
                  onChange={(v) => setCh(i, { ...c, tasks: c.tasks.map((x, j) => (j === k ? v : x)) })}
                  onDelete={() => window.confirm("Slette oppgaven?") && setCh(i, { ...c, tasks: c.tasks.filter((_, j) => j !== k) })} />
              ))}
              <button onClick={() => setCh(i, { ...c, tasks: [...c.tasks, { id: `${c.id}-t${Date.now()}`, title: "Ny oppgave", goal: "", suits: "", tools: "", difficulty: "", time: "", steps: [], mistakes: "", check: "", when_pro: "", status: "utkast" }] })}
                className="mt-4 flex items-center gap-1 text-sm text-[#8C6E48]" data-testid={`add-task-${c.number}`}><Plus className="w-4 h-4" />Ny oppgave</button>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};

const Review = ({ b, persist }) => {
  const tasks = b.chapters.flatMap((c, ci) => c.tasks.map((t, ti) => ({ t, ci, ti, label: `${c.number}. ${t.title}` }))).filter((x) => x.t.steps.length);
  const [sel, setSel] = useState(0);
  const [i, setI] = useState(0);
  const [note, setNote] = useState("");
  const ref = useRef(null);
  const cur = tasks[sel];
  const st = cur?.t.steps[i];
  useEffect(() => { setNote(st?.review_note || ""); }, [sel, i]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!cur) return <p className="text-sm">Ingen oppgaver med steg ennå.</p>;
  const update = async (patch) => {
    const nb = structuredClone(b);
    Object.assign(nb.chapters[cur.ci].tasks[cur.ti].steps[i], patch);
    await persist(nb);
  };
  const upload = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    fd.append("public", "false");
    try {
      const { data } = await http.post("/admin/book-images", fd);
      await update({ image_path: data.storage_path, image_status: "eget foto – venter på godkjenning" });
    } catch (err) { toast.error(errorText(err)); }
    e.target.value = "";
  };
  const done = cur.t.steps.filter((x) => x.image_status === "godkjent").length;
  return (
    <div data-testid="book-review">
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <select value={sel} onChange={(e) => { setSel(Number(e.target.value)); setI(0); }} className="h-10 border border-[#E5E0D8] px-3 text-sm bg-white" data-testid="review-task-select">
          {tasks.map((x, k) => <option key={k} value={k}>{x.label}</option>)}
        </select>
        <p className="text-sm text-[#5C5A56]" data-testid="review-progress">{done} av {cur.t.steps.length} bilder godkjent</p>
      </div>
      <div className="flex gap-1 mb-6 flex-wrap">
        {cur.t.steps.map((x, k) => (
          <button key={x.code} onClick={() => setI(k)} title={x.image_status}
            className={`w-9 h-9 text-xs border ${k === i ? "border-[#1A1A1A]" : "border-[#E5E0D8]"} ${x.image_status === "godkjent" ? "bg-[#1A1A1A] text-white" : x.image_status === "må endres" ? "bg-red-100" : "bg-white"}`}
            data-testid={`review-step-dot-${k + 1}`}>{k + 1}</button>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-10" data-testid={`review-step-${st.code}`}>
        <div>
          {st.image_path ? <img src={fileUrl(st.image_path, getToken())} alt={st.alt} className="w-full aspect-[3/2] object-cover" /> : <div className="w-full aspect-[3/2] bg-[#F0EBE1]" />}
          <p className="text-xs text-[#5C5A56] mt-2 font-mono">{st.code}</p>
        </div>
        <div>
          <p className="eyebrow">Steg {i + 1} av {cur.t.steps.length}</p>
          <h3 className="text-3xl font-medium mt-2">{st.title}</h3>
          <p className="mt-3 text-[#5C5A56] leading-relaxed">{st.text}</p>
          <p className="mt-4 text-sm">Status: <span className="px-2 py-0.5 bg-[#F0EBE1]" data-testid="review-status">{st.image_status}</span></p>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Merknad (hva må endres?)" className="rounded-none mt-4 text-sm" data-testid="review-note" />
          <div className="flex flex-wrap gap-3 mt-4">
            <button onClick={() => update({ image_status: "godkjent", review_note: note })} disabled={!st.image_path} className="px-5 py-2.5 bg-[#1A1A1A] text-white text-sm disabled:opacity-40" data-testid="review-approve-btn">Godkjenn bilde</button>
            <button onClick={() => update({ image_status: "må endres", review_note: note })} className="px-5 py-2.5 border border-red-700 text-red-700 text-sm" data-testid="review-reject-btn">Må endres</button>
            <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} data-testid="review-upload-input" />
            <button onClick={() => ref.current.click()} className="px-5 py-2.5 border border-[#1A1A1A] text-sm flex items-center gap-2" data-testid="review-upload-btn"><Upload className="w-4 h-4" />Bytt med eget foto</button>
          </div>
          <div className="flex justify-between mt-8 text-sm">
            <button onClick={() => setI(Math.max(0, i - 1))} disabled={i === 0} className="disabled:opacity-30" data-testid="review-prev-btn">← Forrige</button>
            <button onClick={() => setI(Math.min(cur.t.steps.length - 1, i + 1))} disabled={i === cur.t.steps.length - 1} className="disabled:opacity-30" data-testid="review-next-btn">Neste →</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Register = ({ b }) => {
  const rows = b.chapters.flatMap((c) => c.tasks.flatMap((t) => t.steps.map((s, i) => ({ ...s, ch: c.number, task: t.title, n: i + 1 }))));
  const ok = (s) => s.image_path && s.image_status === "godkjent";
  const overview = b.chapters.map((c) => ({
    n: c.number, title: c.title, tasks: c.tasks.length,
    complete: c.tasks.filter((t) => t.steps.length > 0 && t.steps.every(ok)).length,
    withSteps: c.tasks.filter((t) => t.steps.length > 0).length,
    steps: c.tasks.reduce((a, t) => a + t.steps.length, 0),
    images: c.tasks.reduce((a, t) => a + t.steps.filter((s) => s.image_path).length, 0),
    approved: c.tasks.reduce((a, t) => a + t.steps.filter(ok).length, 0),
  }));
  return (
    <div className="overflow-x-auto" data-testid="image-register">
      <h3 className="text-xl font-medium mb-3">Status per kapittel</h3>
      <table className="w-full text-sm mb-10" data-testid="book-status-overview">
        <thead><tr className="text-left text-xs uppercase tracking-[0.12em] text-[#5C5A56] border-b border-[#E5E0D8]">
          <th className="py-2 pr-4">Kapittel</th><th className="pr-4">Oppgaver</th><th className="pr-4">Med steg</th><th className="pr-4">Ferdig godkjent</th><th className="pr-4">Steg</th><th className="pr-4">Bilder</th><th>Bilder godkjent</th></tr></thead>
        <tbody>{overview.map((o) => (
          <tr key={o.n} className="border-b border-[#E5E0D8]"><td className="py-2 pr-4">{o.n}. {o.title}</td><td className="pr-4">{o.tasks}</td><td className="pr-4">{o.withSteps}</td><td className="pr-4">{o.complete}</td><td className="pr-4">{o.steps}</td><td className="pr-4">{o.images}</td><td>{o.approved}</td></tr>))}
        </tbody>
      </table>
      <h3 className="text-xl font-medium mb-3">Bilderegister</h3>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs uppercase tracking-[0.12em] text-[#5C5A56] border-b border-[#E5E0D8]">
          <th className="py-2 pr-4">Kode</th><th className="pr-4">Kap.</th><th className="pr-4">Oppgave / steg</th><th className="pr-4">Bildetekst</th><th className="pr-4">Status</th><th>Bilde</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.code} className="border-b border-[#E5E0D8]">
            <td className="py-2 pr-4 font-mono text-xs">{r.code}</td><td className="pr-4">{r.ch}</td><td className="pr-4">{r.task} · {r.n}. {r.title}</td>
            <td className="pr-4">{r.caption}</td>
            <td className="pr-4"><span className={`text-xs px-2 py-0.5 ${r.image_status === "godkjent" ? "bg-[#1A1A1A] text-white" : "bg-[#F0EBE1]"}`}>{r.image_status}</span></td>
            <td>{r.image_path ? "Ja" : "–"}</td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
};

const Access = ({ b }) => {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ email: "", name: "" });
  const load = () => http.get(`/admin/books/${b.id}/purchases`).then((r) => setList(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const grant = async () => {
    try {
      const { data } = await http.post(`/admin/books/${b.id}/grant`, f);
      toast.success(data.email_sent ? "Tilgang gitt og lenke sendt på e-post" : "Tilgang gitt (e-post feilet – kopier lenken)");
      setF({ email: "", name: "" }); load();
    } catch (e) { toast.error(errorText(e)); }
  };
  const revoke = async (id) => { await http.delete(`/admin/books/purchases/${id}`); load(); };
  const base = `${window.location.origin}/handbok/les/`;
  return (
    <div data-testid="book-access">
      <p className="text-sm text-[#5C5A56] mb-4 max-w-2xl">Til nettbetaling er koblet til: gi tilgang manuelt etter betaling (f.eks. Vipps/SumUp-terminal). Kunden får en personlig lenke på e-post.</p>
      <div className="flex flex-wrap gap-3 p-4 bg-[#F0EBE1]">
        <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Navn" className={`${inp} bg-white w-48`} data-testid="grant-name-input" />
        <Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="E-post" className={`${inp} bg-white w-64`} data-testid="grant-email-input" />
        <button onClick={grant} className="px-5 bg-[#1A1A1A] text-white text-sm" data-testid="grant-btn">Gi tilgang</button>
      </div>
      <div className="mt-6 border-t border-[#E5E0D8]">
        {list.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center gap-4 py-3 border-b border-[#E5E0D8] text-sm" data-testid={`purchase-${p.id}`}>
            <span className="w-56">{p.name || "–"} · {p.email}</span>
            <span className="text-xs text-[#5C5A56]">{new Date(p.created_at).toLocaleString("nb-NO")}</span>
            <span className={`text-xs ${p.active ? "text-green-800" : "text-red-700"}`}>{p.active ? "Aktiv" : "Trukket tilbake"}</span>
            {p.active && <a href={base + p.token} target="_blank" rel="noreferrer" className="text-xs underline">Åpne lenke</a>}
            {p.active && <button onClick={() => revoke(p.id)} className="text-xs text-red-700 ml-auto">Trekk tilbake</button>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default function BooksAdmin() {
  const [b, setB] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { http.get("/admin/books").then((r) => setB(r.data[0] || false)); }, []);
  if (b === null) return <p className="text-sm">Laster…</p>;
  if (!b) return <p className="text-sm">Ingen bok ennå.</p>;
  const persist = async (book, msg = "Lagret") => {
    try { const { id, created_at, updated_at, ...body } = book; const { data } = await http.put(`/admin/books/${id}`, body); setB(data); toast.success(msg); } catch (e) { toast.error(errorText(e)); }
  };
  const save = async () => { setSaving(true); await persist(b, "Håndboken er lagret"); setSaving(false); };
  return (
    <div data-testid="admin-books">
      <AdminTitle title="Håndbok" sub="Rediger kapitler, oppgaver og steg. Bilder merket «generert – må kontrolleres» er AI-illustrasjoner og må sjekkes eller byttes med egne foto før salg.">
        <div className="flex gap-3">
          <a href="/handbok" target="_blank" rel="noreferrer" className="flex items-center gap-2 px-4 py-2.5 border border-[#1A1A1A] text-sm"><ExternalLink className="w-4 h-4" />Produktside</a>
          <button onClick={save} disabled={saving} className="px-6 py-2.5 bg-[#1A1A1A] text-white text-sm disabled:opacity-50" data-testid="book-save-btn">{saving ? "Lagrer…" : "Lagre håndbok"}</button>
        </div>
      </AdminTitle>
      <Tabs defaultValue="content">
        <TabsList className="rounded-none">
          <TabsTrigger value="content" className="rounded-none" data-testid="book-tab-content">Innhold</TabsTrigger>
          <TabsTrigger value="review" className="rounded-none" data-testid="book-tab-review">Gjennomgang av bilder</TabsTrigger>
          <TabsTrigger value="register" className="rounded-none" data-testid="book-tab-register">Bilderegister</TabsTrigger>
          <TabsTrigger value="access" className="rounded-none" data-testid="book-tab-access">Tilgang og salg</TabsTrigger>
        </TabsList>
        <TabsContent value="content" className="pt-6"><ContentEditor b={b} setB={setB} /></TabsContent>
        <TabsContent value="review" className="pt-6"><Review b={b} persist={persist} /></TabsContent>
        <TabsContent value="register" className="pt-6"><Register b={b} /></TabsContent>
        <TabsContent value="access" className="pt-6"><Access b={b} /></TabsContent>
      </Tabs>
    </div>
  );
}
